import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { Account, AccountSearchParams } from "../../../../../../services/management/account/types";
import { AppDispatch } from "../../../../../../shared/redux/types";
import { accountApi } from "../../../../../../services/management/account/api";
import Combobox from "../../../../../../shared/components/redesigned/Combobox";

type AccountSelectProps = {
    onChange: (option: Account | null) => void;
    value: Account | null;
    placeholder?: string;
};

const AccountSelect = React.forwardRef<HTMLInputElement, AccountSelectProps>(
    ({ onChange, value, placeholder }: AccountSelectProps, ref) => {
        const dispatch = useDispatch<AppDispatch>();
        const [searchKey, setSearchKey] = useState<string>("");
        const searchArgs = useMemo<AccountSearchParams>(
            () => ({ filter: { code: searchKey } }),
            [searchKey],
        );

        const accountsSearchFn = useCallback(
            (args: typeof searchArgs) => {
                dispatch(accountApi.endpoints.getAccounts.initiate(args));
            },
            [dispatch],
        );

        const debouncedAccountsSearchFn = useMemo(
            () =>
                debounce((args: typeof searchArgs) => {
                    accountsSearchFn(args);
                }, 500),
            [accountsSearchFn],
        );

        useEffect(() => {
            debouncedAccountsSearchFn(searchArgs);
        }, [searchArgs, debouncedAccountsSearchFn]);

        const { data, isFetching } = accountApi.endpoints.getAccounts.useQueryState(searchArgs);

        return (
            <Combobox<Account>
                loading={isFetching}
                inputRef={ref}
                items={data ? data.items : []}
                onInputValueChange={(val) => setSearchKey(val || "")}
                onChange={onChange}
                selectedItem={value}
                placeholder={placeholder}
                itemToString={(item) => item?.code || ""}
                getOptionLabel={(item) => item.code}
                getOptionKey={(item) => item.code}
                getOptionSelected={(selected, item) => selected?.code === item.code}
            />
        );
    },
);

export default AccountSelect;
